import { getObjLength, buildGenerateQuestionsPayload } from "./utils";
import { propertiesAction } from "../../redux/slices/properties/propertiesSlice";

export const generatePropertySecurityQuestions = (
  dispatch,
  propertyData,
  userId,
) => {
  if (!propertyData?.id && !propertyData?.property_id) return;

  if (propertyData?.from_risk_measure) {
    dispatch(
      propertiesAction.securityPropertiesQueDetails({
        url: "properties/generate-questions",
        data: {
          ...buildGenerateQuestionsPayload(
            propertyData,
            userId || propertyData?.owner_id,
          ),
          from_resume: true,
          step: 2,
          step_key: "GENERATE_QUESTIONS",
          risk_report_id: propertyData.id,
        },
        token: true,
      }),
    );
    return;
  }

  if (propertyData?.id) {
    dispatch(
      propertiesAction.securityPropertiesQueDetails({
        url: `customer/generate-questions/${propertyData.id}`,
        data: {
          from_risk_measure: propertyData.from_risk_measure,
          user_id: userId || propertyData?.owner_id,
        },
        token: true,
      }),
    );
  }
};

/** Customer /properties add-property: generate questions after CP order summary. */
export const generateCustomerAddPropertyCompleteProtectionQuestions = (
  dispatch,
  genearteQuesPayload,
  propertyQuestion = {},
  property = {},
) => {
  const payload = getObjLength(genearteQuesPayload)
    ? genearteQuesPayload
    : buildGenerateQuestionsPayload(
        property,
        property?.user_id || property?.owner_id,
      );

  if (!getObjLength(payload) || !payload?.property_id) {
    return false;
  }

  const riskReportId =
    propertyQuestion?.data?.risk_report_id ??
    propertyQuestion?.data?.report_id ??
    propertyQuestion?.data?.id;

  dispatch(propertiesAction.setSecurityPropertiesQueData({}));
  dispatch(
    propertiesAction.securityPropertiesQueDetails({
      url: "properties/generate-questions",
      data: {
        ...payload,
        step: 2,
        step_key: "GENERATE_QUESTIONS",
        category: "complete_protection",
        ...(riskReportId ? { risk_report_id: riskReportId } : {}),
      },
      token: true,
    }),
  );
  return true;
};

export const hasPropertyGeneratedQuestions = (propertyQuestion, property) => {
  if (
    !getObjLength(propertyQuestion) ||
    !propertyQuestion?.success ||
    !Array.isArray(propertyQuestion?.data?.questions) ||
    propertyQuestion.data.questions.length === 0
  ) {
    return false;
  }
  const questionPropertyId =
    propertyQuestion?.data?.property_id ?? propertyQuestion?.data?.id;
  const currentPropertyId = property?.property_id ?? property?.id;
  if (questionPropertyId && currentPropertyId) {
    return String(questionPropertyId) === String(currentPropertyId);
  }
  return true;
};

export const isUpgradeFromEnhancedMonitoring = (property) =>
  Boolean(
    property?.from_risk_measure ||
      property?.category === "enhanced_monitoring" ||
      property?.product_tier === "enhanced_monitoring",
  );

export const resolveEnhancedUpgradeRiskReportId = (property) => {
  if (!property) return null;
  // Prefer explicit report ids (customer property list / Plan_Data rows).
  const explicitId =
    property.risk_report_id ||
    property.report_id ||
    property.subscription?.risk_report_id ||
    property.subscription?.report_id ||
    null;
  if (explicitId) {
    return explicitId;
  }
  // Risk-measure workspace rows use `id` as the report id.
  if (property.from_risk_measure && property.id) {
    return property.id;
  }
  return null;
};

const readStoredPlanData = () => {
  try {
    return JSON.parse(localStorage.getItem("Plan_Data") || "{}") || {};
  } catch {
    return {};
  }
};

/**
 * Both upgrade_from_enhanced and risk_report_id are required for the
 * Enhanced → Complete Protection upgrade credit. Prefer Plan_Data (quote),
 * then property / propertyQuestion fields.
 */
export const resolveEnhancedToCompleteUpgradeFlags = ({
  property,
  propertyQuestion,
  planData,
  forceUpgrade = false,
  riskReportId = null,
} = {}) => {
  const plan =
    planData && typeof planData === "object" ? planData : readStoredPlanData();

  const planHasUpgrade =
    plan?.upgrade_from_enhanced === true ||
    plan?.upgrade_from_enhanced === 1 ||
    plan?.upgrade_from_enhanced === "true";

  const resolvedRiskReportId =
    riskReportId ||
    plan?.risk_report_id ||
    resolveEnhancedUpgradeRiskReportId(property) ||
    propertyQuestion?.data?.risk_report_id ||
    propertyQuestion?.data?.report_id ||
    (property?.from_risk_measure ? property?.id : null) ||
    null;

  const shouldUpgrade =
    forceUpgrade ||
    planHasUpgrade ||
    isUpgradeFromEnhancedMonitoring(property);

  if (
    !shouldUpgrade ||
    resolvedRiskReportId == null ||
    resolvedRiskReportId === ""
  ) {
    return null;
  }

  return {
    upgrade_from_enhanced: true,
    risk_report_id: resolvedRiskReportId,
  };
};

/** Attach Enhanced → CP discount flags when both can be resolved. */
export const attachEnhancedToCompleteUpgradeFlags = (
  payload,
  sources = {},
) => {
  if (!payload || typeof payload !== "object") {
    return payload;
  }
  const flags = resolveEnhancedToCompleteUpgradeFlags(sources);
  if (!flags) {
    return payload;
  }
  return { ...payload, ...flags };
};

export const buildCompleteProtectionFinalAmountPayload = ({
  subscriptionPlans,
  property,
  userId,
  upgradeFromEnhanced = false,
  riskReportId = null,
}) => {
  const find_plan = subscriptionPlans?.data?.find(
    (data) => data?.product_tier === "complete_protection",
  );
  if (!find_plan) return null;

  let data = {
    plan_id: parseInt(find_plan?.plan_id, 10),
    user_id: userId,
    property_id: [
      property?.complete_protection_property_id || property?.property_id,
    ],
    property_ids: [
      {
        address: property?.address,
        property_id:
          property?.complete_protection_property_id || property?.property_id,
        city: property?.city,
        state: property?.state,
        zip: property?.zip,
      },
    ],
  };

  data = attachEnhancedToCompleteUpgradeFlags(data, {
    property,
    forceUpgrade: upgradeFromEnhanced,
    riskReportId,
  });

  // Promo applies to both new Complete Protection and Enhanced → CP upgrades.
  const promoCode = localStorage.getItem("promoCode");
  if (promoCode?.length > 0) {
    data.promo_code = promoCode;
  }

  return { data, find_plan, url: "customer/get-final-amount" };
};
